/** Staff console home: priority card, platform-at-a-glance, tenants / partner applications / feature flags panels. Real endpoints only; each block is role-gated and degrades on 403. */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Button, Heading, Panel, Text, ToneChip, ToneTile, toneAt, type Tone } from '@qm/ui';
import type { Flag } from '@qm/contracts/flags';
import { Icon } from '../../../shell/Icon';
import { useSurface } from '../kit/context';
import { errorInfo } from '../kit/http';
import { useResource, type Resource } from '../kit/resource';
import { EmptyState, ErrorNote, Screen, When } from '../kit/ui';
import { isMaskedTenant, type ApplicationView, type TenantRow } from '../api';
import { canSupport, isSuperAdmin } from '../lib/logic';
import { FLAG_TONE, avatarTone, buildWorkQueue, clockOf, countLabel, flagState, flagsForHome, flagsSummary, greetingFor, healthSummary, homePriority, initialsOf, pendingApplications, tenantStats, tenantTone, weekOf } from '../lib/home-logic';
import { useAdminApi } from './layout';
import './home.css';

type TenantEnv = { data: TenantRow[]; meta?: { nextCursor?: string | null } };
const PEEK = 5;

/** A block the signed-in role may not see (403) or that the server does not serve (404) is dropped, not shown as an error. */
const hidden = (r: Resource<unknown>) => r.status === 'error' && [401, 403, 404].includes(errorInfo(r.error).status ?? 0);

function HomePanel({ id, title, foot, children }: { id: string; title: string; foot?: { to: string; label: string }; children: ReactNode }) {
  return (
    <Panel className="ah-panel" aria-labelledby={`${id}-h`} data-testid={`home-${id}`}>
      <div className="ah-panel__head"><Heading level={2} id={`${id}-h`}>{title}</Heading></div>
      {children}
      {foot ? <Link className="ah-panel__foot" to={foot.to}>{foot.label}<span aria-hidden="true">{' >'}</span></Link> : null}
    </Panel>
  );
}

function PanelBody<T>({ r, children, empty }: { r: Resource<T>; children: (d: T) => ReactNode; empty?: (d: T) => boolean }) {
  const { t } = useSurface();
  if (r.status === 'loading') return <Text tone="muted" aria-busy="true">{t('portal.kit.loading')}</Text>;
  if (r.status === 'error') return <><ErrorNote error={r.error} /><Button onClick={r.reload}>{t('portal.kit.retry')}</Button></>;
  if (empty?.(r.data)) return <EmptyState message={t('portal.admin.home.empty')} />;
  return <>{children(r.data)}</>;
}

export function AdminHome() {
  const { t, roles, now, locale } = useSurface();
  const api = useAdminApi();
  const supportView = canSupport(roles);
  const adminView = isSuperAdmin(roles);
  const tenants = useResource<TenantEnv>('home.tenants', () => api.tenants({}) as Promise<TenantEnv>);
  const apps = useResource<ApplicationView[] | null>('home.applications', () => (supportView ? api.applications('pending') : Promise.resolve(null)));
  const flags = useResource<Flag[] | null>('home.flags', () => (adminView ? api.flags() : Promise.resolve(null)));

  const rows = tenants.status === 'ready' ? tenants.data.data : [];
  const more = tenants.status === 'ready' && !!tenants.data.meta?.nextCursor;
  const stats = tenantStats(rows);
  const pending = apps.status === 'ready' && apps.data ? pendingApplications(apps.data) : null;
  const fl = flags.status === 'ready' && flags.data ? flagsSummary(flags.data) : null;
  const showApps = supportView && !hidden(apps);
  const showFlags = adminView && !hidden(flags);
  const showTenants = !hidden(tenants);
  const priority = homePriority(pending ? pending.ready : null, tenants.status === 'ready' ? stats.suspendedAutomation : null);

  const val = (r: Resource<unknown>, v: string | null) => (r.status === 'ready' && v !== null ? v : '-');
  const tiles: { key: string; label: string; value: string; sub: string; to: string; icon: 'team' | 'approvals' | 'settings'; show: boolean }[] = [
    { key: 'tenants', label: t('portal.admin.home.stat.tenants'), value: val(tenants, countLabel(stats.loaded, more)), sub: t('portal.admin.home.stat.tenantsSub', { n: stats.active }), to: '/tenants', icon: 'team', show: showTenants },
    { key: 'suspended', label: t('portal.admin.home.stat.suspended'), value: val(tenants, countLabel(stats.suspendedAutomation, more)), sub: t('portal.admin.home.stat.suspendedSub'), to: '/tenants?automation=suspended', icon: 'settings', show: showTenants },
    { key: 'partnerClients', label: t('portal.admin.home.stat.partnerClients'), value: val(tenants, countLabel(stats.partnerClients, more)), sub: t('portal.admin.home.stat.partnerClientsSub'), to: '/tenants?view=partners', icon: 'team', show: showTenants },
    { key: 'applications', label: t('portal.admin.home.stat.applications'), value: val(apps, pending ? String(pending.list.length) : null), sub: t('portal.admin.home.stat.applicationsSub', { n: pending?.ready ?? 0 }), to: '/partners', icon: 'approvals', show: showApps },
    { key: 'flags', label: t('portal.admin.home.stat.flags'), value: val(flags, fl ? String(fl.total) : null), sub: t('portal.admin.home.stat.flagsSub', { n: fl?.killed ?? 0 }), to: '/flags', icon: 'settings', show: showFlags },
  ];
  const shown = tiles.filter((x) => x.show);

  const today = now();
  const tag = locale === 'ar' ? 'ar-AE-u-ca-gregory-nu-latn' : 'en-AE-u-ca-gregory';
  const dateLabel = new Intl.DateTimeFormat(tag, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(today);
  const dayName = new Intl.DateTimeFormat(tag, { weekday: 'short', timeZone: 'UTC' });
  const week = weekOf(today).map((d) => ({ ...d, label: dayName.format(new Date(`${d.iso}T00:00:00Z`)) }));
  const queue = buildWorkQueue(apps.status === 'ready' ? apps.data : null, rows);
  const health = healthSummary(rows);
  const host = typeof window === 'undefined' ? 'admin.quixomarketing.com' : window.location.host;
  const asOf = new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }).format(today);
  const refresh = () => { tenants.reload(); apps.reload(); flags.reload(); };
  const priorityCount = priority.kind === 'clear' ? 0 : priority.count;

  return (
    <Screen title={t(`portal.admin.home.greeting.${greetingFor(today.getHours())}`)} subtitle={dateLabel} testId="admin-home">
      <div className="ah qm-dotgrid">
       <div className="qs-split">
        <div className="ah-main">
        <section className="qs-reco ah-priority" aria-labelledby="home-priority-h" data-testid="home-priority" data-kind={priority.kind}>
          <ToneTile tone={priority.kind === 'clear' ? 'mint' : priority.kind === 'suspended' ? 'coral' : 'sun'} size="md"><Icon name={priority.kind === 'clear' ? 'approvals' : 'bell'} size={18} /></ToneTile>
          <div className="qs-reco__copy ah-priority__copy">
            <Text tone="muted">{t('portal.admin.home.priority.label')}</Text>
            <Heading level={2} id="home-priority-h">{priority.kind === 'clear' ? t('portal.admin.home.priority.clear') : t('portal.admin.home.reco.title', { n: priorityCount })}</Heading>
            <Text tone="muted">{priority.kind === 'clear' ? t('portal.admin.home.priority.clearNote') : `${t(`portal.admin.home.priority.${priority.kind}`, { n: priorityCount })}. ${t(`portal.admin.home.priority.${priority.kind}Note`)}`}</Text>
          </div>
          {priority.kind !== 'clear' ? <Link className="qm-button" data-variant="primary" to={priority.kind === 'applications' ? '/partners' : '/tenants?automation=suspended'}>{priority.kind === 'applications' ? t('portal.admin.home.reco.approvalsCta') : t('portal.admin.home.priority.suspendedCta')}</Link> : null}
        </section>

        <section className="ah-weekbar" aria-labelledby="home-week-h" data-testid="home-week">
          <div className="ah-weekbar__head">
            <Heading level={2} id="home-week-h">{t('portal.admin.home.week.label')}</Heading>
            <span className="qa-sample" data-testid="home-calendar-na">{t('portal.admin.home.week.calendar')}: {t('portal.admin.home.pulse.notConnected')}</span>
          </div>
          <ol className="qs-week" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {week.map((d) => (
              <li key={d.iso} className="qs-week__day" data-today={d.today ? 'true' : undefined} aria-current={d.today ? 'date' : undefined}>
                <small>{d.label}</small><b>{d.date}</b>
              </li>
            ))}
          </ol>
        </section>

        <Panel className="ah-panel" aria-labelledby="home-queue-h" data-testid="home-queue">
          <div className="ah-panel__head">
            <Heading level={2} id="home-queue-h">{t('portal.admin.home.queue.title')}</Heading>
            <ToneChip tone="lavender">{queue.length}</ToneChip>
          </div>
          {queue.length === 0 ? <EmptyState message={t('portal.admin.home.queue.empty')} /> : (
            <ul className="qs-queue">
              {queue.slice(0, 6).map((q) => (
                <li key={q.id} className="ah-queue-row" data-kind={q.kind}>
                  <span className="qs-queue__time">{clockOf(q.at)}</span>
                  <span className="qs-avatar" data-tone={avatarTone(q.title)} aria-hidden="true">{initialsOf(q.title)}</span>
                  <Link className="qs-queue__title ah-queue-link" to={q.to}>
                    <strong>{q.title}</strong>
                    <small>{q.account ? `${q.account} · ` : ''}{t(`portal.admin.home.queue.sub.${q.kind}`)}</small>
                  </Link>
                  <span className="qs-queue__meta">
                    <ToneChip tone={q.kind === 'approval' ? 'lavender' : q.kind === 'automation' ? 'coral' : 'sun'}>{t(`portal.admin.home.queue.kind.${q.kind}`)}</ToneChip>
                    <small>{q.dueToday ? t('portal.admin.home.queue.dueToday') : t('portal.admin.home.queue.open')}</small>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {shown.length > 0 ? (
          <section aria-labelledby="home-glance-h" data-testid="home-stats">
            <Heading level={2} id="home-glance-h">{t('portal.admin.home.glance')}</Heading>
            <ul className="ah-stats">
              {shown.map((s, i) => (
                <li key={s.key}>
                  <Link className="ah-stat" to={s.to} data-testid={`home-stat-${s.key}`}>
                    <ToneTile tone={toneAt(i)}><Icon name={s.icon} size={16} /></ToneTile>
                    <span className="ah-stat__copy"><span className="ah-stat__label">{s.label}</span><b>{s.value}</b><span className="ah-stat__sub">{s.sub}</span></span>
                  </Link>
                </li>
              ))}
            </ul>
            {more ? <Text tone="muted">{t('portal.admin.home.moreNote')}</Text> : null}
          </section>
        ) : null}

        <div className="ah-grid">
          {showTenants ? (
            <HomePanel id="tenants" title={t('portal.admin.home.tenants')} foot={{ to: '/tenants', label: t('portal.admin.home.tenantsAll') }}>
              <PanelBody r={tenants} empty={(d) => d.data.length === 0}>
                {(d) => (
                  <ul className="ah-list">
                    {d.data.slice(0, PEEK).map((r) => {
                      const masked = isMaskedTenant(r);
                      return (
                        <li key={r.id}>
                          <ToneTile tone={tenantTone(r.status)} size="sm"><Icon name="team" size={14} /></ToneTile>
                          <span className="ah-stack">
                            <Link to={`/tenants/${encodeURIComponent(r.id)}`}>{masked ? r.opaqueId : r.name}</Link>
                            <small>{masked ? t('portal.admin.tenants.partnerClient') : r.plan ?? t('portal.admin.home.noPlan')}</small>
                          </span>
                          <ToneChip tone={tenantTone(r.status)}>{r.status}</ToneChip>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </PanelBody>
            </HomePanel>
          ) : null}

          {showApps ? (
            <HomePanel id="applications" title={t('portal.admin.home.applications')} foot={{ to: '/partners', label: t('portal.admin.home.applicationsAll') }}>
              <PanelBody r={apps} empty={(d) => !d || pendingApplications(d).list.length === 0}>
                {(d) => (
                  <ul className="ah-list">
                    {pendingApplications(d ?? []).list.slice(0, PEEK).map((a, i) => (
                      <li key={a.id}>
                        <ToneTile tone={toneAt(i)} size="sm"><Icon name="approvals" size={14} /></ToneTile>
                        <span className="ah-stack">
                          <Link to={`/partners/${encodeURIComponent(a.id)}`}>{a.companyName}</Link>
                          <small>{a.country} · <When iso={a.createdAt} /></small>
                        </span>
                        <ToneChip tone={a.readyAt ? 'mint' : 'sun'}>{t(a.readyAt ? 'portal.admin.home.ready' : 'portal.admin.home.inVetting')}</ToneChip>
                      </li>
                    ))}
                  </ul>
                )}
              </PanelBody>
            </HomePanel>
          ) : null}

          {showFlags ? (
            <HomePanel id="flags" title={t('portal.admin.home.flags')} foot={{ to: '/flags', label: t('portal.admin.home.flagsAll') }}>
              <PanelBody r={flags} empty={(d) => !d || d.length === 0}>
                {(d) => (
                  <ul className="ah-list">
                    {flagsForHome(d ?? [], PEEK).map((f) => {
                      const s = flagState(f);
                      const tone: Tone = FLAG_TONE[s];
                      return (
                        <li key={f.key}>
                          <span className="ah-stack"><code>{f.key}</code><small>{f.description}</small></span>
                          <ToneChip tone={tone}>{t(`portal.admin.home.flag.${s}`)}</ToneChip>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </PanelBody>
            </HomePanel>
          ) : null}
        </div>
        </div>

        <aside className="qs-aside ah-pulse" aria-labelledby="home-pulse-h" data-testid="home-pulse">
          <div className="qs-aside__head"><Heading level={2} id="home-pulse-h">{t('portal.admin.home.pulse.title')}</Heading></div>
          <div className="qs-pulse">
            <div className="qs-pulse__block" data-testid="pulse-health">
              <div className="qs-pulse__head"><span className="qs-micro">{t('portal.admin.home.pulse.health')}</span></div>
              {tenants.status === 'ready' && health.total > 0 ? (
                <>
                  <span className="qs-pulse__big">{t('portal.admin.home.pulse.healthyOf', { n: health.healthy, total: health.total })}</span>
                  <div className="qs-ramp" role="img" aria-label={t('portal.admin.home.pulse.healthyOf', { n: health.healthy, total: health.total })}>
                    <span style={{ flexGrow: health.atRisk }} /><span style={{ flexGrow: health.watch }} /><span style={{ flexGrow: health.healthy }} />
                  </div>
                  <div className="qs-pulse__subs">
                    <div><b>{health.atRisk}</b><small>{t('portal.admin.home.pulse.atRisk')}</small></div>
                    <div><b>{health.watch}</b><small>{t('portal.admin.home.pulse.watch')}</small></div>
                    <div><b>{health.healthy}</b><small>{t('portal.admin.home.pulse.healthy')}</small></div>
                  </div>
                  <p className="qa-note">{t('portal.admin.home.pulse.healthNote')}</p>
                </>
              ) : <span className="qa-sample">{t('portal.admin.home.pulse.notConnected')}</span>}
            </div>
            {(['leads', 'deliverables', 'reports'] as const).map((k) => (
              <div className="qs-pulse__block" key={k} data-testid={`pulse-${k}`}>
                <div className="qs-pulse__head"><span className="qs-micro">{t(`portal.admin.home.pulse.${k}`)}</span><span className="qa-sample">{t('portal.admin.home.pulse.notConnected')}</span></div>
                <span className="qs-pulse__big qa-na" aria-hidden="true">-</span>
              </div>
            ))}
          </div>
          <Link className="ah-panel__foot" to="/tenants">{t('portal.admin.home.pulse.details')}<span aria-hidden="true">{' >'}</span></Link>
        </aside>
       </div>

        <footer className="ah-foot" data-testid="home-foot">
          <span>{t('portal.admin.home.roles', { roles: roles.map((r) => t(`portal.admin.role.${r}`)).join(', ') })}</span>
          <span>{t('portal.admin.home.foot.audit')}</span>
          <span>{t('portal.admin.home.foot.host')}: {host}</span>
          <span>{t('portal.admin.home.foot.tz')}: UTC</span>
          <span>{t('portal.admin.home.foot.asOf', { when: asOf })}</span>
          <Button className="ah-foot__refresh" onClick={refresh}>{t('portal.admin.home.foot.refresh')}</Button>
        </footer>
      </div>
    </Screen>
  );
}
